/**
 * Ultra-Modern Layout Component
 * World-Class SaaS ERP Platform Design
 */

import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { SupportAgentWidget } from '../shared/SupportAgentWidget';
import {
  initWatermark,
  destroyWatermark,
  applyBlurProtection,
  applyScreenShareBlock,
} from '@euroasiann/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function TemplateLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const policy = user.contentProtection ?? {
      screenshotAllowed: true,
      screenRecordingAllowed: true,
      screenShareAllowed: true,
    };

    if (!policy.screenshotAllowed) {
      initWatermark({
        userName: `${user.firstName} ${user.lastName}`,
        userRole: user.role,
        ip: user.ip ?? 'unknown',
      });
    }

    applyBlurProtection(!policy.screenRecordingAllowed);

    applyScreenShareBlock(!policy.screenShareAllowed, () => {
      fetch(`${API_URL}/api/v1/tech/audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          action: 'screen_share_blocked',
          userId: user._id,
          portal: 'tech',
          ts: new Date().toISOString(),
        }),
      }).catch(() => {});
    });

    return () => {
      destroyWatermark();
      applyBlurProtection(false);
      applyScreenShareBlock(false);
    };
  }, [user]);

  return (
    <div className="flex h-screen w-full bg-[hsl(var(--background))] text-[hsl(var(--foreground))] overflow-hidden relative app-scaling">
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggle={(collapsed) => setSidebarCollapsed(collapsed)} 
      />
      <div
        className={cn(
          'flex-1 flex flex-col h-full transition-all duration-300 ease-in-out bg-[hsl(var(--background))]',
          sidebarCollapsed ? 'ml-20' : 'ml-72'
        )}
      >
        <Header
          sidebarCollapsed={sidebarCollapsed}
          onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className="flex-1 overflow-y-auto bg-[hsl(var(--background))] pt-16 relative">
          <div className="w-full min-h-full p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
      <SupportAgentWidget />
    </div>
  );
}
